import cn from "classnames"
import React, { useState, useEffect } from "react"
import { useTranslation, withTranslation, type WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import Slider from "@/common/components/slider"
import { useFeatureIsOn } from "@/common/growthbook-react"
import ShapeIcons from "@/images/signomatic/shapes"
import {
  validateCornerRadius,
  CORNER_RADIUS_MIN_MM,
  CORNER_RADIUS_DEFAULT_MAX_MM,
  cornerRadiusFixmethodConditions,
  maxCornerRadiusBySize,
} from "@/signomatic/app/fixmethods/custom_holes"
import { optionForcedState } from "@/signomatic/app/sign_validator"
import AutoGrid from "@/signomatic/components/auto_grid"
import { type SliderMinMax } from "@/signomatic/components/fixmethod_toolbox"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { type State } from "@/signomatic/store/reducers"
import { matchArray } from "@/signomatic/util"

interface CornerRadiusSliderRange {
  radius: SliderMinMax
}

const reason = (type: string | null) => {
  switch (type) {
    case "size":
      return "toolbox.shape_disabled_size"
    case "fixmethod":
      return "toolbox.shape_disabled_fix_method"
    default:
      return ""
  }
}

interface ShapeProps {
  name?: string | null
  enabled?: boolean | null
  active: boolean
  update: () => void
}

const Shape = ({ name, enabled, active, update }: ShapeProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("item", "selection", `shape-${name}`, { disabled: !enabled, active })} onClick={update}>
      <div className="image">{name && ShapeIcons[name] ? React.createElement(ShapeIcons[name]) : ""}</div>
      <div className="name paragraph-2xs center mb-0">{t(`shape_type.${name}`)}</div>
    </div>
  )
}

interface ShapeToolboxStateProps {
  data: SignData
  signState: SignState
}

type ShapeToolboxProps = ShapeToolboxStateProps & ToolboxStripItemProps & WithTranslation

const ShapeToolbox: React.FC<ShapeToolboxProps> = ({ sign, signState, data, t }) => {
  const { width, height, material, shape, cornerRadiusMm } = signState
  const [disabledMessage, setDisabledMessage] = useState<string | null>()
  const cornerRadiusFlag = useFeatureIsOn("corner-radius-9390")
  const cornerRadiusConditions =
    cornerRadiusFlag &&
    cornerRadiusFixmethodConditions(signState) &&
    shape === "rect" &&
    (sign.signType === "label_plates" || material === "stainless") &&
    material !== "brass"
  const [cornerRadiusRange, setCornerRadiusRange] = useState<CornerRadiusSliderRange>({
    radius: { min: CORNER_RADIUS_MIN_MM, max: CORNER_RADIUS_DEFAULT_MAX_MM },
  })

  const handleCornerRadiusChange = (newRadius: number) => {
    const radiusRes = validateCornerRadius(signState, newRadius)

    sign.updateState({
      cornerRadiusMm: radiusRes.value,
    })

    setCornerRadiusRange({
      radius: { min: CORNER_RADIUS_MIN_MM, max: radiusRes.max },
    })
  }

  useEffect(() => {
    if (!signState || !cornerRadiusConditions) return

    const radiusRes = validateCornerRadius(signState, cornerRadiusMm || CORNER_RADIUS_MIN_MM)

    setCornerRadiusRange({
      radius: { min: CORNER_RADIUS_MIN_MM, max: radiusRes.max },
    })
  }, [signState, cornerRadiusConditions, cornerRadiusMm])

  const renderShapes = () => {
    let contour = data.shapes.find(s => s.name === "contour")

    if (contour && contour.filters && contour.filters.admin && !ClientData.admin) {
      contour = undefined
    }

    return (
      <>
        <div className="inline-toolbox">
          <AutoGrid>
            {data.shapes.map(shape => {
              if (shape.name === "contour") {
                return null
              }

              if (
                shape.name === "round" &&
                cornerRadiusFlag &&
                (sign.signType === "label_plates" || material === "stainless") &&
                material !== "brass" &&
                cornerRadiusFixmethodConditions(signState) &&
                !ClientData.loadSignType
              ) {
                return null
              }

              let enabled = shape.enabled
              let forcedState = {}

              if (!enabled && matchArray(shape.disabled_reason, ["size"])) {
                enabled = true

                forcedState = optionForcedState(shape, signState, ["size"])
              }

              return (
                <Shape
                  key={shape.name}
                  name={shape.name}
                  active={signState.shape === shape.name}
                  enabled={enabled}
                  update={() => {
                    if (enabled) {
                      sign.updateState({ shape: shape.name, ...forcedState })
                      setDisabledMessage(null)
                    } else if (shape.disabled_reason) {
                      setDisabledMessage(shape.disabled_reason[0])
                    }
                  }}
                />
              )
            })}
          </AutoGrid>
          {disabledMessage && (
            <div className="disabled-reason paragraph-2xs bold mb-0">{t(reason(disabledMessage))}</div>
          )}
          {cornerRadiusConditions && (
            <div className="pt-6">
              <Slider
                inputId="corner_radius"
                inputName="corner_radius_range"
                label={t("toolbox.corner_radius")}
                className="mb-4"
                min={CORNER_RADIUS_MIN_MM}
                max={maxCornerRadiusBySize(width, height)}
                value={cornerRadiusMm || CORNER_RADIUS_MIN_MM}
                hideRangeLabels
                inputRight
                onChange={value => {
                  handleCornerRadiusChange(Math.max(0, value))
                }}
                enabledMin={cornerRadiusRange.radius.min}
                enabledMax={cornerRadiusRange.radius.max}
              />
            </div>
          )}
        </div>
        {contour && (
          <div className="inline-toolbox sub">
            <h4>{t("shape_type.contour")}</h4>
            <AutoGrid>
              <Shape
                name="contour_small"
                active={signState.shape === contour.name && signState.contour === "s"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "s" })
                  setDisabledMessage(null)
                }}
              />
              <Shape
                name="contour_medium"
                active={signState.shape === contour.name && signState.contour === "m"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "m" })
                  setDisabledMessage(null)
                }}
              />
              <Shape
                name="contour_large"
                active={signState.shape === contour.name && signState.contour === "l"}
                enabled
                update={() => {
                  sign.updateState({ shape: contour.name, contour: "l" })
                  setDisabledMessage(null)
                }}
              />
            </AutoGrid>
          </div>
        )}
      </>
    )
  }

  return window.innerWidth <= 1023 ? (
    <Toolbox tKey="shape">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {renderShapes()}
      </Scrollable>
    </Toolbox>
  ) : (
    <Toolbox tKey="shape">{renderShapes()}</Toolbox>
  )
}

export default connect(({ signState, currentSignData }: State) => ({
  signState,
  data: currentSignData,
}))(withTranslation()(ShapeToolbox))
